import { useFlags } from 'launchdarkly-react-client-sdk';
import { useCallback, useEffect, useState } from 'react';
import Communication from '../../utils/Communication';
import Logger from '../../utils/Logger';
import Util from '../../utils/Util';
import { consumeUspsCopFlow } from './uspsCopFlow';

const USPS_BUSINESS_RATE_SET_TYPE = 'NSA';

// Remove authorization_code from URL to prevent re-authorization attempts
const removeAuthorizationCodeFromUrl = () => {
	const url = new URL(window.location.href);
	if (url.searchParams.has('authorization_code')) {
		url.searchParams.delete('authorization_code');
		window.history.replaceState({}, '', url.toString());
	}
};

// The authorization_code this page load has already taken (redeemed or rejected).
// Module-level, like the Lazarus hook's guard, so remounts cannot re-consume.
let handledAuthorizationCode = null;

const useUspsBusinessAccount = () => {
	const redirectUri =
		'https://print.stamps.com/webpostage/ajax/CompleteUSPSOnboarding.aspx';

	const { canConnectToUspsBusiness } = useFlags();

	const [isLoading, setIsLoading] = useState(false);
	const [isEligible, setIsEligible] = useState(false);
	const [onboardingStatus, setOnboardingStatus] = useState(null);
	const [userInfoToken, setUserInfoToken] = useState(null);
	const [error, setError] = useState(null);

	async function fetchUspsBusinessOnboardingStatus() {
		const onboardingResponse =
			await Communication.getUspsBusinessOnboardingStatus();
		const { data } = onboardingResponse;
		let authorizationCode = Util.getQueryParamByName('authorization_code');
		if (authorizationCode && authorizationCode === handledAuthorizationCode) {
			// An earlier run (remount / double-invoked effect) already took this code;
			// the marker is single-use, so a second consume would wrongly reject it.
			authorizationCode = null;
		} else if (authorizationCode) {
			handledAuthorizationCode = authorizationCode;
			// CSRF defense: only redeem a code this tab asked for. A code arriving
			// via a link (no marker from "Link Your Business Account") is dropped.
			// Consume whenever a code is present (even if a token already exists) so
			// the single-use marker is never left armed.
			if (!consumeUspsCopFlow()) {
				Logger.logToServer({
					category: 'usps_business_cop_code_rejected',
					reason: 'no_matching_flow_started_in_this_tab',
				});
				removeAuthorizationCodeFromUrl();
				authorizationCode = null;
			}
		}
		if (!userInfoToken && authorizationCode) {
			const authorizeResponse = await Communication.authorizeUspsBusinessToken(
				authorizationCode,
				redirectUri
			);
			if (Communication.isSuccessful(authorizeResponse)) {
				setUserInfoToken(authorizeResponse?.data?.Token);
				setOnboardingStatus({ ...data, IsOnboarded: true });
			} else {
				if (authorizeResponse.ErrorDescription.startsWith('{')) {
					const error = JSON.parse(authorizeResponse.ErrorDescription);
					setError({
						data: {
							...error,
							description: error.detail ?? error.description ?? error.title,
						},
					});
				} else {
					setError({ response: authorizeResponse });
				}
				setOnboardingStatus(data);
				return;
			}
		} else {
			setOnboardingStatus(data);
		}
	}
	const checkUspsBusinessAccess = useCallback(async () => {
		setIsLoading(true);
		setError(null);

		try {
			const profileResponse = await Communication.getInternalProfile();
			const { ResellerAccountId, RateSetType } = profileResponse.data;

			const uspsBusinessEligible =
				ResellerAccountId != null &&
				RateSetType === USPS_BUSINESS_RATE_SET_TYPE;
			setIsEligible(uspsBusinessEligible);

			Logger.logToServer({
				category: 'profile_info',
				canConnectToUspsBusiness,
				ProfileRateSetType: RateSetType,
				ProfileResellerAccountId: ResellerAccountId,
			});

			if (!uspsBusinessEligible) {
				return;
			}
			await fetchUspsBusinessOnboardingStatus();
		} catch (error) {
			setError(error);
			setIsEligible(false);
			setOnboardingStatus(null);
		} finally {
			setIsLoading(false);
		}
	}, []);

	useEffect(() => {
		if (canConnectToUspsBusiness) {
			checkUspsBusinessAccess();
		}
	}, []);

	const clearUserInfoToken = () => {
		setUserInfoToken(null);
		removeAuthorizationCodeFromUrl();
	};

	return {
		canConnectToUspsBusiness,
		isEligible,
		redirectUri,
		onboardingStatus,
		userInfoToken,
		isLoading,
		error,
		setError,
		refetch: fetchUspsBusinessOnboardingStatus,
		clearUserInfoToken,
	};
};

export default useUspsBusinessAccount;
